import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of, tap } from 'rxjs';
import { AppConfig, Sport, PricingTier, HelloassoFacilite } from '../models/registration.models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private config: AppConfig = { sports: [], pricing: [], helloassoFacilites: [], passSportReduction: 0, passSportActif: true, maxMembers: 0, attestationPaiementUrl: undefined };
  private http = inject(HttpClient);

  readonly conditionsHtml = signal<string>('');
  readonly configError = signal<string | null>(null);

  /** Appelé au démarrage via APP_INITIALIZER — charge les tarifs depuis le Sheet. */
  loadRemoteConfig(): Promise<void> {
    const url = environment.appsScriptUrl;
    if (!url) return Promise.resolve();

    return new Promise(resolve => {
      this.http.get<{ pricing?: PricingTier[]; helloassoFacilites?: HelloassoFacilite[]; passSportReduction?: number; passSportActif?: boolean; sports?: Sport[]; maxMembers?: number; conditionsHtml?: string; attestationPaiementUrl?: string }>(url)
        .pipe(
          tap(remote => {
            if (remote?.pricing?.length) {
              this.config = { ...this.config, pricing: remote.pricing };
            }
            if (remote?.helloassoFacilites?.length) {
              this.config = { ...this.config, helloassoFacilites: remote.helloassoFacilites };
            }
            if (remote?.passSportReduction != null) {
              this.config = { ...this.config, passSportReduction: remote.passSportReduction };
            }
            if (remote?.passSportActif != null) {
              this.config = { ...this.config, passSportActif: remote.passSportActif };
            }
            if (remote?.sports?.length) {
              this.config = { ...this.config, sports: remote.sports };
            }
            if (remote?.maxMembers != null && remote.maxMembers > 0) {
              this.config = { ...this.config, maxMembers: remote.maxMembers };
            }
            if (remote?.conditionsHtml) {
              this.conditionsHtml.set(remote.conditionsHtml);
            }
            if (remote?.attestationPaiementUrl) {
              this.config = { ...this.config, attestationPaiementUrl: remote.attestationPaiementUrl };
            }
            this.configError.set(null);
          }),
          catchError(() => {
            this.configError.set('Impossible de charger la configuration. Vérifiez votre connexion et rechargez la page.');
            return of(null);
          }),
        )
        .subscribe(() => resolve());
    });
  }

  get sports(): Sport[] {
    return this.config.sports;
  }

  get pricing(): PricingTier[] {
    return this.config.pricing;
  }

  get helloassoFacilites(): HelloassoFacilite[] {
    return this.config.helloassoFacilites;
  }

  get passSportReduction(): number {
    return this.config.passSportReduction;
  }

  get passSportActif(): boolean {
    return this.config.passSportActif;
  }

  get maxMembers(): number {
    return this.config.maxMembers;
  }

  get appsScriptUrl(): string {
    return environment.appsScriptUrl;
  }

  get clubName(): string {
    return environment.clubName;
  }

  get logoFile(): string {
    return environment.logoFile;
  }

  get dossierPrefix(): string {
    return environment.dossierPrefix;
  }

  get attestationPaiementUrl(): string | undefined {
    return this.config.attestationPaiementUrl;
  }

  getTarifForMembers(count: number): PricingTier | undefined {
    return this.config.pricing.find(p => p.memberCount === count);
  }

  getPriceForMembers(count: number): number {
    return this.getTarifForMembers(count)?.price ?? 0;
  }

}
